import { ArrowDown, Calculator, CheckCircle2, Clock, DollarSign, FileSignature, FileText, Lock, ShieldCheck } from "lucide-react";
import { StatusPill } from "@/components/StatusPill";

/* ============================================================
 * §3 · WIN MORE WORK — clean rebuild (Desktop EN authoritative)
 *   - Single environmental canvas at z-0 (desktop only)
 *   - Left zone: headline + subhead + body + 3 capability cards
 *                + 1 contractor-control card
 *   - Right zone: Estimate → Proposal → Contract vertical
 *                 progression, IN-FLOW inside the grid (NOT
 *                 position:absolute) so Chromium's tiled
 *                 full-page screenshot cannot ghost/duplicate it
 *   - Bottom: 4-item benefit strip
 *   - No section header, no scrims/overlays/gradients, single
 *     instance of every element
 * ============================================================ */
export const WinSection = ({ c }) => {
  const cards = [
    {
      icon: Calculator,
      title: c.win.c1_title,
      body: c.win.c1_body,
      right: (
        <div className="text-right">
          <div className="text-[17px] lg:text-[15px] font-bold text-[var(--jc-text)] leading-tight">$7,450</div>
          <div className="text-[12px] lg:text-[10.5px] text-[var(--jc-text-soft)] uppercase tracking-wider">{c.win.c1_metric_label}</div>
        </div>
      ),
      testid: "win-card-estimate",
    },
    {
      icon: FileText,
      title: c.win.c2_title,
      body: c.win.c2_body,
      right: <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.win.c2_status : k)} />,
      testid: "win-card-proposal",
    },
    {
      icon: FileSignature,
      title: c.win.c3_title,
      body: c.win.c3_body,
      right: <StatusPill status="signed" kind="contract" t={(k) => (k === "status.signed" ? c.win.c3_status : k)} />,
      testid: "win-card-contract",
    },
  ];
  const value = [
    { icon: Lock,         t: c.win.value[0].t, b: c.win.value[0].b, testid: "win-value-1" },
    { icon: Clock,        t: c.win.value[1].t, b: c.win.value[1].b, testid: "win-value-2" },
    { icon: CheckCircle2, t: c.win.value[2].t, b: c.win.value[2].b, testid: "win-value-3" },
    { icon: DollarSign,   t: c.win.value[3].t, b: c.win.value[3].b, testid: "win-value-4" },
  ];

  return (
    <section data-testid="features-win" className="relative lg:min-h-[900px] isolate overflow-hidden">
      {/* Desktop-only environmental canvas at z-0. Mobile uses a
          separate in-flow image block below. */}
      <img
        src="/brand/features/win_env_canvas.png"
        alt="Levi at his office desk with tablet, Winning Jobs notebook, JobClose AI phone, BUILT BY A CONTRACTOR wall sign, and PHIL 4:13 detail behind him."
        draggable={false}
        data-testid="win-env-canvas"
        className="hidden lg:block absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
        style={{ zIndex: 0 }}
      />

      {/* Content — z-2 above canvas. Outer wrapper is FULL-WIDTH
          (not max-w-7xl) so the left column can hug the left edge
          of the section (into the pale-blue negative-space panel)
          and the right column can hug the right edge (into the
          wall-sign area, clear of Levi). No absolute-positioned
          children — pure grid, no tiled-screenshot ghosting. */}
      <div className="relative w-full px-6 lg:px-[5%] pt-16 pb-12 lg:pt-20 lg:pb-16" style={{ zIndex: 2 }}>
        <div className="grid grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* LEFT — hugs left edge; sits inside the pale-blue panel */}
          <div className="col-span-12 lg:col-span-6" data-testid="win-left-zone">
            <h2 className="font-display tracking-tight text-[#0A1428] text-4xl sm:text-5xl lg:text-[54px] leading-[1.02]" data-testid="win-headline">
              {c.win.h2a} <span className="text-[var(--jc-blue)]">{c.win.h2b}</span>
            </h2>
            <p className="mt-4 text-[19px] font-semibold text-[#0A1428] leading-snug max-w-lg" data-testid="win-subhead">
              {c.win.leader}
            </p>
            <p className="mt-3 text-[15.5px] leading-[1.55] text-[var(--jc-text-soft)] max-w-lg" data-testid="win-body">
              {c.win.lede}
            </p>

            <div className="mt-8 space-y-4 max-w-[640px] lg:max-w-[540px]">
              {cards.map((cc) => (
                <div
                  key={cc.testid}
                  data-testid={cc.testid}
                  className="rounded-2xl bg-white/95 border border-white shadow-md p-6 lg:p-6 flex items-center gap-4 lg:gap-5"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                    <cc.icon size={28} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[18px] lg:text-[17.5px] font-bold leading-tight text-[var(--jc-text)]">{cc.title}</div>
                    <div className="text-[15.5px] lg:text-[14px] text-[var(--jc-text-soft)] leading-snug mt-2 lg:mt-1.5">{cc.body}</div>
                  </div>
                  <div className="flex-shrink-0">{cc.right}</div>
                </div>
              ))}

              <div
                className="rounded-2xl bg-white/95 border border-white shadow-md p-6 lg:p-6 flex items-start gap-4 lg:gap-5"
                data-testid="win-your-pricing"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-[var(--jc-blue)] grid place-items-center text-white">
                  <ShieldCheck size={28} strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-[18px] lg:text-[17px]">
                    <span className="text-[var(--jc-blue)] font-bold">{c.win.pricing_a}</span>{" "}
                    <span className="text-[var(--jc-text)] font-bold">{c.win.pricing_b} </span>
                    <span className="text-[var(--jc-blue)] font-bold">{c.win.pricing_c}</span>
                  </div>
                  <div className="text-[15.5px] lg:text-[14px] text-[var(--jc-text-soft)] mt-2 lg:mt-1.5">{c.win.pricing_sub}</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — hugs right edge; sits over the wall-sign area,
              clear of Levi's face/hat/collar/paws/tablet. In-flow
              inside the grid column (no position:absolute). */}
          <div
            className="hidden lg:block lg:col-span-6"
            data-testid="win-right-zone"
          >
            <div className="ml-auto w-[340px] lg:w-[300px] space-y-3.5 lg:space-y-3 lg:mt-24" data-testid="win-progression">
              <div className="rounded-xl bg-white border border-white shadow-xl p-4 lg:p-3.5" data-testid="win-progression-estimate">
                <div className="flex items-center gap-2.5 pb-2.5 border-b border-[var(--jc-border)]/60">
                  <div className="w-8 h-8 lg:w-7 lg:h-7 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><Calculator size={16} /></div>
                  <span className="font-semibold text-[14px] lg:text-[13px] flex-1">{c.win.prog_est_title}</span>
                  <span className="font-semibold text-[14px] lg:text-[13px]">$7,450.00</span>
                </div>
                <div className="mt-2.5 text-[13.5px] lg:text-[12.5px] font-semibold text-[var(--jc-text)]">{c.win.prog_est_line}</div>
                <div className="mt-1.5 text-[12.5px] lg:text-[11.5px] text-[var(--jc-text-soft)] space-y-1">
                  <div className="flex justify-between"><span>{c.win.prog_est_materials}</span><span>$3,650.00</span></div>
                  <div className="flex justify-between"><span>{c.win.prog_est_labor}</span><span>$2,550.00</span></div>
                  <div className="flex justify-between"><span>{c.win.prog_est_profit}</span><span>$1,250.00</span></div>
                  <div className="flex justify-between pt-1.5 mt-1 border-t border-[var(--jc-border)]/60 text-[var(--jc-text)] font-semibold">
                    <span>{c.win.prog_est_total}</span><span>$7,450.00</span>
                  </div>
                </div>
              </div>

              <div className="flex justify-center text-[var(--jc-blue)]" aria-hidden>
                <ArrowDown size={18} strokeWidth={2.5} />
              </div>

              <div className="rounded-xl bg-white border border-white shadow-xl p-4 lg:p-3.5" data-testid="win-progression-proposal">
                <div className="flex items-center gap-2.5 pb-2.5 border-b border-[var(--jc-border)]/60">
                  <div className="w-8 h-8 lg:w-7 lg:h-7 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><FileText size={16} /></div>
                  <span className="font-semibold text-[14px] lg:text-[13px] flex-1">{c.win.prog_prop_title}</span>
                  <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.win.prog_prop_status : k)} />
                </div>
                <div className="mt-2.5 text-[13.5px] lg:text-[12.5px] text-[var(--jc-text-soft)] leading-snug">{c.win.prog_prop_body}</div>
              </div>

              <div className="flex justify-center text-[var(--jc-blue)]" aria-hidden>
                <ArrowDown size={18} strokeWidth={2.5} />
              </div>

              <div className="rounded-xl bg-white border border-white shadow-xl p-4 lg:p-3.5" data-testid="win-progression-contract">
                <div className="flex items-center gap-2.5 pb-2.5 border-b border-[var(--jc-border)]/60">
                  <div className="w-8 h-8 lg:w-7 lg:h-7 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><FileSignature size={16} /></div>
                  <span className="font-semibold text-[14px] lg:text-[13px] flex-1">{c.win.prog_con_title}</span>
                  <StatusPill status="signed" kind="contract" t={(k) => (k === "status.signed" ? c.win.prog_con_status : k)} />
                </div>
                <div className="mt-2.5 text-[13.5px] lg:text-[12.5px] text-[var(--jc-text-soft)] leading-snug">{c.win.prog_con_body}</div>
                <div className="mt-1.5 text-[18px] lg:text-[16px] italic text-[var(--jc-text)]" style={{ fontFamily: "'Segoe Script','Bradley Hand',cursive" }}>M. Johnson</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom benefit strip — DESKTOP ONLY. Mobile renders its
            own stacked benefits below the mobile Levi visual. */}
        <div className="hidden lg:block max-w-7xl mx-auto mt-12 lg:mt-16">
          <div
            className="rounded-2xl bg-white/95 border border-white shadow-lg px-7 lg:px-9 py-5 lg:py-6"
            data-testid="win-value-strip"
          >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {value.map((v) => (
              <div key={v.testid} data-testid={v.testid} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                  <v.icon size={24} strokeWidth={2.2} />
                </div>
                <div>
                  <div className="text-[16.5px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                  <div className="text-[13.5px] text-[var(--jc-text-soft)] leading-snug mt-1">{v.b}</div>
                </div>
              </div>
            ))}
          </div>
          </div>
        </div>
      </div>

      {/* MOBILE ONLY · dedicated vertical stack — Levi visual +
          Estimate→Proposal→Contract progression + four stacked
          benefits. Desktop layout above is untouched. */}
      <div className="lg:hidden max-w-7xl mx-auto px-6 pb-8 space-y-6" data-testid="win-mobile-env">
        {/* Levi featured environmental photograph — cropped toward
            the right-hand Levi/office portion (no desktop pale-blue
            negative-space appears in the mobile crop). */}
        <div className="w-full h-[440px] rounded-2xl overflow-hidden shadow-xl bg-[#0B1E3E]" data-testid="win-mobile-levi">
          <img
            src="/brand/features/win_env_canvas.png"
            alt="Levi at his office desk with tablet, Winning Jobs notebook, phone, wall sign and office environment."
            draggable={false}
            className="w-full h-full object-cover select-none"
            style={{ objectPosition: "72% 55%", transform: "scale(1.3)", transformOrigin: "70% 55%" }}
          />
        </div>

        {/* Estimate → Proposal → Contract, stacked vertically */}
        <div className="space-y-4" data-testid="win-progression-mobile">
          <div className="rounded-xl bg-white border border-[var(--jc-border)] shadow-lg p-5">
            <div className="flex items-center gap-3 pb-3 border-b border-[var(--jc-border)]/60">
              <div className="w-11 h-11 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><Calculator size={22} /></div>
              <span className="font-semibold text-[17px] flex-1">{c.win.prog_est_title}</span>
              <span className="font-semibold text-[17px]">$7,450.00</span>
            </div>
            <div className="mt-3 text-[16px] font-semibold text-[var(--jc-text)]">{c.win.prog_est_line}</div>
            <div className="mt-2.5 text-[15px] text-[var(--jc-text-soft)] space-y-1.5">
              <div className="flex justify-between"><span>{c.win.prog_est_materials}</span><span>$3,650.00</span></div>
              <div className="flex justify-between"><span>{c.win.prog_est_labor}</span><span>$2,550.00</span></div>
              <div className="flex justify-between"><span>{c.win.prog_est_profit}</span><span>$1,250.00</span></div>
              <div className="flex justify-between pt-2 mt-1.5 border-t border-[var(--jc-border)]/60 text-[var(--jc-text)] font-semibold text-[16px]">
                <span>{c.win.prog_est_total}</span><span>$7,450.00</span>
              </div>
            </div>
          </div>

          <div className="flex justify-center text-[var(--jc-blue)]" aria-hidden>
            <ArrowDown size={22} strokeWidth={2.5} />
          </div>

          <div className="rounded-xl bg-white border border-[var(--jc-border)] shadow-lg p-5">
            <div className="flex items-center gap-3 pb-3 border-b border-[var(--jc-border)]/60">
              <div className="w-11 h-11 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><FileText size={22} /></div>
              <span className="font-semibold text-[17px] flex-1">{c.win.prog_prop_title}</span>
              <StatusPill status="sent" kind="estimate" t={(k) => (k === "status.sent" ? c.win.prog_prop_status : k)} />
            </div>
            <div className="mt-3 text-[15.5px] text-[var(--jc-text-soft)] leading-snug">{c.win.prog_prop_body}</div>
          </div>

          <div className="flex justify-center text-[var(--jc-blue)]" aria-hidden>
            <ArrowDown size={22} strokeWidth={2.5} />
          </div>

          <div className="rounded-xl bg-white border border-[var(--jc-border)] shadow-lg p-5">
            <div className="flex items-center gap-3 pb-3 border-b border-[var(--jc-border)]/60">
              <div className="w-11 h-11 rounded-md bg-[var(--jc-blue)] grid place-items-center text-white"><FileSignature size={22} /></div>
              <span className="font-semibold text-[17px] flex-1">{c.win.prog_con_title}</span>
              <StatusPill status="signed" kind="contract" t={(k) => (k === "status.signed" ? c.win.prog_con_status : k)} />
            </div>
            <div className="mt-3 text-[15.5px] text-[var(--jc-text-soft)] leading-snug">{c.win.prog_con_body}</div>
            <div className="mt-2 text-[20px] italic text-[var(--jc-text)]" style={{ fontFamily: "'Segoe Script','Bradley Hand',cursive" }}>M. Johnson</div>
          </div>
        </div>

        {/* Four benefit items stacked vertically */}
        <div className="rounded-2xl bg-white border border-[var(--jc-border)] shadow-lg p-6 space-y-6" data-testid="win-value-strip-mobile">
          {value.map((v) => (
            <div key={`m-${v.testid}`} className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#EEF3FE] grid place-items-center text-[var(--jc-blue)]">
                <v.icon size={24} strokeWidth={2.2} />
              </div>
              <div>
                <div className="text-[17px] font-bold text-[var(--jc-text)] leading-tight">{v.t}</div>
                <div className="text-[15.5px] text-[var(--jc-text-soft)] leading-snug mt-1.5">{v.b}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
